import { createContext, useContext, useState, useEffect } from 'react';
import { fetchAllHomeData } from './homeDataService';

const HomeDataContext = createContext(null);

export const HomeDataProvider = ({ children }) => {
  // Initialize from localStorage synchronously to prevent skeleton flash on refresh
  const [data, setData] = useState(() => {
    try {
      const cached = localStorage.getItem('bento_home_data');
      return cached ? JSON.parse(cached) : null;
    } catch (e) {
      return null;
    }
  });
  
  // Only loading if we don't have cached data
  const [isLoading, setIsLoading] = useState(!data);

  useEffect(() => {
    let isMounted = true;

    // This fetch runs in the background. It will hit Firestore's IndexedDB cache 
    // instantly, and also silently fetch fresh data from the server.
    fetchAllHomeData().then((result) => {
      if (isMounted && result) {
        setData(result);
        setIsLoading(false);
        try {
          localStorage.setItem('bento_home_data', JSON.stringify(result));
        } catch (e) {
          console.error("Failed to cache home data", e);
        }
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <HomeDataContext.Provider value={{ data, isLoading }}>
      {children}
    </HomeDataContext.Provider>
  );
};

/**
 * Hook to access home screen data.
 * Returns { data, isLoading } where data contains all section data.
 * 
 * When used OUTSIDE HomeDataProvider (e.g. Body7 on About page),
 * returns { data: null, isLoading: true } — component should
 * fall back to its own fetch in that case.
 */
export const useHomeData = () => {
  const context = useContext(HomeDataContext);
  // If used outside provider, return a loading-state object
  // so components can gracefully fall back to their own fetch
  if (!context) {
    return { data: null, isLoading: true, isOutsideProvider: true };
  }
  return { ...context, isOutsideProvider: false };
};
